<script>
    import InputText from "@/components/InputText.vue";

    export default {
        extends: InputText,
        name: "InputNumber",
        props: {
            value: {
                type: [Number, String], // Used both types to make sure that if backend has saved and returning different types
                default: undefined,
            },
            min: {
                type: Number,
                default: 0,
            },
            max: {
                type: Number,
                default: Infinity,
            },
            decimals: {
                type: Number,
                default: null,
            },
        },
        data() {
            return {
                count: this.formatValue(this.value),
            };
        },
        watch: {
            // Without watcher the count value is not updating from this.value.
            value: function (newValue) {
                if (this.formatValue(newValue) !== this.count) {
                    this.count = this.formatValue(newValue);
                }
            },

            count(newValue, oldValue) {
                if (newValue === oldValue) {
                    return;
                }

                const formattedValue = this.formatValue(newValue);

                if (!formattedValue || this.validateValue(formattedValue, this.decimals)) {
                    this.count = formattedValue;
                } else {
                    this.count = oldValue;
                }

                this.emitValue(this.count);
            },
        },
        computed: {
            isMinReached() {
                return this.count <= this.min;
            },
            isMaxReached() {
                return this.count >= this.max;
            },
        },
        methods: {
            validateValue: (value, decimals) => {
                // Allow numbers with 0-1 commas and 0-1 numbers following the comma
                const defaultRegex = /^[0-9]+([,]?[0-9]?)?$/;

                if (decimals) {
                    const decimalsRegex = new RegExp(`^[0-9]+([,]?[0-9]{0,${decimals}})?$`);
                    return decimalsRegex.test(value);
                }

                return defaultRegex.test(value);
            },
            /**
             * Updates the count by either incrementing or decrementing it.
             *
             * @param {boolean} increment - If true, the count will be incremented. If false, the count will be decremented.
             */
            stepCount(increment) {
                const currentCount = this.count?.replace(",", ".") ?? this.count;

                // Convert the string to a number
                let countAsNumber = parseFloat(currentCount);

                // If NaN (e.g., empty input), set to -1 for incrementing to 0
                if (isNaN(countAsNumber)) {
                    countAsNumber = -1;
                }

                // Increment or decrement the number
                if (Number.isInteger(countAsNumber)) {
                    countAsNumber = countAsNumber + (increment ? 1 : -1);
                } else {
                    countAsNumber = countAsNumber + (increment ? 0.1 : -0.1);
                }

                // Convert the number back to a string
                let updatedCountAsString = countAsNumber.toString();

                // Ensure one decimal place if it was a decimal number
                if (!Number.isInteger(countAsNumber)) {
                    updatedCountAsString = countAsNumber.toFixed(1);
                }

                // Replace period with comma if necessary
                if (this.count.includes(",")) {
                    updatedCountAsString = updatedCountAsString.replace(".", ",");
                }

                this.count = updatedCountAsString;
            },
            stepUp() {
                if (!this.isMaxReached) {
                    this.stepCount(true);
                }
            },
            stepDown() {
                if (!this.isMinReached) {
                    this.stepCount(false);
                }
            },
            formatValue(value) {
                if (typeof value === "string") {
                    return value.replace(".", ",");
                }

                return value;
            },
            emitValue(newValue) {
                if (!this.validateValue(newValue, this.decimals)) {
                    if (typeof newValue === "undefined" || newValue === null || newValue === "") {
                        this.$emit("input", null);
                    }

                    return;
                }

                if (typeof newValue === "string") {
                    // Convert comma to dot for backend parsing
                    const formattedValue = newValue.replace(",", ".");

                    this.$emit("input", formattedValue === "" ? null : formattedValue);

                    return;
                }

                this.$emit("input", newValue);
            },
        },
    };
</script>
<template>
    <div class="input-number" :class="klass">
        <input type="text" v-model="count" :class="klass" :min="min" :max="max" v-bind="$attrs" autocomplete="off" />
        <div class="buttons" :class="klass">
            <button type="button" class="step-up" @click="stepUp" :disabled="$attrs['disabled'] || isMaxReached">
                +
            </button>
            <button type="button" class="step-down" @click="stepDown" :disabled="$attrs['disabled'] || isMinReached">
                -
            </button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    @import "@/style/mixins/input";

    .input-number {
        display: inline-flex;

        width: 100%;

        input {
            -moz-appearance: textfield;
            appearance: textfield;

            &::-webkit-inner-spin-button,
            &::-webkit-outer-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            @include input-mixin;
            padding-right: 0;
            border-top-right-radius: 0 !important;
            border-bottom-right-radius: 0 !important;

            &.error {
                border-bottom: $input-border-error;
            }
        }

        .buttons {
            width: 22px;
            display: flex;
            flex-direction: column;
            border-top-right-radius: $border-radius;
            border-bottom-right-radius: $border-radius;
            border-top: $input-border;
            border-right: $input-border;
            border-bottom: $input-border;

            &.error {
                border-bottom: $input-border-error;
            }

            &:has(button:disabled) {
                opacity: $disabled-opacity;
            }

            button {
                height: 23px;
                border: 0;
                background-color: transparent;
                cursor: pointer;
                text-align: center;

                &:disabled {
                    cursor: default;
                }

                &.step-up {
                    border-bottom: $input-border;
                }

                &.step-down {
                }
            }
        }
    }
</style>
